<template>
  <div class="price-ticker" :class="{ 'is-ready': rows.length, 'is-light': !isDarkMode }">
    <div class="ticker-live" title="Aktuální kurzy">
      <span class="ticker-live-dot"></span>
      <span class="ticker-live-label">KURZY</span>
    </div>
    <div class="ticker-viewport">
      <div
        v-if="rows.length"
        class="ticker-track"
        :style="{ animationDuration: durationSec + 's' }"
      >
        <div v-for="copy in 2" :key="'copy-' + copy" class="ticker-copy">
          <button
            v-for="row in rows"
            :key="copy + '-' + (row.ISINID || row.ISIN)"
            type="button"
            class="ticker-item"
            :title="row.Nm + (row.ISIN ? ' · ' + row.ISIN : '')"
            @click="openChart(row)"
          >
            <span
              class="ticker-logo"
              :class="{ 'is-fallback': !showShareLogo(row) }"
              :style="logoStyle(row)"
            >
              <img
                v-if="showShareLogo(row)"
                :src="logoUrl(row)"
                alt=""
                @error="onLogoError(row.ISIN)"
              />
              <span v-else>{{ shareMonogram(row.Nm) }}</span>
            </span>
            <span class="ticker-name">{{ shortName(row.Nm) }}</span>
            <span class="ticker-price">{{ formatPrice(row.Prc) }}</span>
            <span class="ticker-chg" :class="changeClass(row)">{{ formatPct(row) }}</span>
          </button>
        </div>
      </div>
      <div v-else class="ticker-placeholder">Načítám kurzy…</div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import dataService from "@/services/dataService";
import { formatTrimZeroCents } from "@/utils/NumberFormat";
import { shareLogoUrl, shareMonogram } from "@/helpers/shareLogos";

export default {
  name: "price-ticker",
  data() {
    return {
      rows: [],
      reloadTimer: null,
      logoFailed: {}
    };
  },
  computed: {
    ...mapState(["isDarkMode"]),
    durationSec() {
      return Math.max(103, this.rows.length * 6.22);
    }
  },
  created() {
    this.loadPrices();
    this.reloadTimer = setInterval(() => {
      this.loadPrices();
    }, 3600000);
  },
  beforeUnmount() {
    if (this.reloadTimer) clearInterval(this.reloadTimer);
  },
  methods: {
    ...mapActions(["fetchISINChartData"]),
    shareMonogram,
    logoUrl(row) {
      return shareLogoUrl(row && row.ISIN);
    },
    showShareLogo(row) {
      const isin = row && row.ISIN;
      return !!(shareLogoUrl(isin) && !this.logoFailed[isin]);
    },
    onLogoError(isin) {
      if (!isin || this.logoFailed[isin]) return;
      this.logoFailed = Object.assign({}, this.logoFailed, { [isin]: true });
    },
    logoStyle(row) {
      if (this.showShareLogo(row)) return {};
      const color = (row && row.Clr) || "#64748b";
      return { backgroundColor: color + "29", color: color };
    },
    asArray(value) {
      if (Array.isArray(value)) return value;
      if (!value || typeof value !== "object") return [];
      return Object.values(value).filter((row) => row && typeof row === "object" && !Array.isArray(row));
    },
    async loadPrices() {
      try {
        const response = await dataService.GetISINList();
        if (response && response.Result) {
          this.rows = this.asArray(response.Data).filter((row) => row.ISINID || row.ISIN);
        }
      } catch (error) {
        console.error(error);
      }
    },
    shortName(name) {
      const text = String(name || "").trim();
      if (text.length <= 16) return text;
      return text.slice(0, 15) + "…";
    },
    formatPrice(value) {
      const formatted = formatTrimZeroCents(value);
      return formatted ? formatted + " Kč" : "—";
    },
    formatPct(row) {
      const n = Number(String(row.PctDf == null ? "" : row.PctDf).replace(",", "."));
      if (!Number.isFinite(n)) return "0,00 %";
      const sign = n > 0 ? "+" : "";
      return sign + n.toLocaleString("cs-CZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " %";
    },
    changeClass(row) {
      const t = Number(row.PrcDfType);
      if (t === 1) return "is-up";
      if (t === -1) return "is-down";
      return "is-flat";
    },
    openChart(row) {
      if (!row || !row.ISINID) return;
      this.fetchISINChartData(row);
    }
  }
};
</script>

<style scoped>
.price-ticker {
  display: flex;
  align-items: stretch;
  min-width: 0;
  flex: 1 1 auto;
  height: 34px;
  border-radius: 12px;
  overflow: hidden;
  background: #0B1220;
  border: 1px solid rgba(255, 138, 76, 0.28);
  box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.35);
}

.ticker-live {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 0 12px;
  color: #FF8A4C;
  font-size: 0.62rem;
  font-weight: 900;
  letter-spacing: 0.12em;
  border-right: 1px solid #1E293B;
  background: linear-gradient(180deg, #111827 0%, #0B1220 100%);
  z-index: 2;
}

.ticker-live-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #FF8A4C;
  box-shadow: 0 0 0 0 rgba(255, 138, 76, 0.7);
  animation: ticker-pulse 1.6s ease-out infinite;
}

.ticker-viewport {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  mask-image: linear-gradient(90deg, transparent 0, #000 18px, #000 calc(100% - 22px), transparent 100%);
}

.ticker-track {
  display: flex;
  align-items: center;
  width: max-content;
  height: 100%;
  animation-name: ticker-scroll;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
}

.price-ticker:hover .ticker-track {
  animation-play-state: paused;
}

.ticker-copy {
  display: flex;
  align-items: center;
  height: 100%;
}

.ticker-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 100%;
  padding: 0 18px;
  border: 0;
  border-right: 1px solid rgba(51, 65, 85, 0.7);
  background: transparent;
  color: #E2E8F0;
  white-space: nowrap;
  cursor: pointer;
  font-family: inherit;
  line-height: 1;
}

.ticker-logo {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.42rem;
  font-weight: 800;
  letter-spacing: -0.04em;
  background: #1E293B;
}

.ticker-logo img {
  width: 16px;
  height: 16px;
  object-fit: contain;
  display: block;
}

.ticker-item:hover {
  background: rgba(255, 138, 76, 0.08);
}

.ticker-name {
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.02em;
  color: #CBD5E1;
}

.ticker-price {
  font-size: 0.72rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #F8FAFC;
}

.ticker-chg {
  font-size: 0.68rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}

.ticker-chg.is-up {
  color: #34D399;
}

.ticker-chg.is-down {
  color: #F87171;
}

.ticker-chg.is-flat {
  color: #94A3B8;
}

.ticker-placeholder {
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 12px;
  color: #64748B;
  font-size: 0.72rem;
  font-weight: 600;
}

@keyframes ticker-scroll {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-50%);
  }
}

@keyframes ticker-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 138, 76, 0.65);
  }
  70% {
    box-shadow: 0 0 0 7px rgba(255, 138, 76, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 138, 76, 0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .ticker-track,
  .ticker-live-dot {
    animation: none;
  }
}

.price-ticker.is-light {
  background: #FFF7ED;
  border-color: #FED7AA;
  box-shadow: none;
}

.price-ticker.is-light .ticker-live {
  color: #EA580C;
  border-right-color: #FED7AA;
  background: linear-gradient(180deg, #FFFBEB 0%, #FFF7ED 100%);
}

.price-ticker.is-light .ticker-item {
  color: #334155;
  border-right-color: #E2E8F0;
}

.price-ticker.is-light .ticker-item:hover {
  background: rgba(255, 138, 76, 0.1);
}

.price-ticker.is-light .ticker-logo {
  background: #E2E8F0;
}

.price-ticker.is-light .ticker-name {
  color: #475569;
}

.price-ticker.is-light .ticker-price {
  color: #0F172A;
}

.price-ticker.is-light .ticker-chg.is-up {
  color: #059669;
}

.price-ticker.is-light .ticker-chg.is-down {
  color: #DC2626;
}

.price-ticker.is-light .ticker-chg.is-flat {
  color: #64748B;
}

.price-ticker.is-light .ticker-placeholder {
  color: #94A3B8;
}

@media (max-width: 430px) {
  .ticker-live {
    padding: 0 8px;
    gap: 0;
  }
  .ticker-live-label {
    display: none;
  }
}
</style>
